import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';

import { useCanaryVisibilityScoreByCompany } from '@/api/aiVisibility';
import WidgetError from '@/components/errors/WidgetError';
import Flare, { Gauge } from '@/components/Flare';
import { Panel } from '@/components/panels';
import { Skeleton } from '@/components/Skeleton';
import { numberFormat } from '@/helper/numberFormatter';

import AIVisibilityInsightsSummary from './AIVisibilityInsightsSummary';
import AIVisibilityOverviewCompetitorTable from './AIVisibilityOverviewCompetitorTable';
import AIVisibilityOverviewProductAreaChart from './AIVisibilityOverviewProductAreaChart';
import AIVisibilityOverviewTopGaps from './AIVisibilityOverviewTopGaps';
import AIVisibilityOverviewTrendChart from './AIVisibilityOverviewTrendChart';
import AIVisibilityProviderChart from './AIVisibilityProviderChart';

const AIVisibilityOverview = () => {
  const { scores, isLoading, error } = useCanaryVisibilityScoreByCompany();
  const visibilityScore = scores?.customer?.overallScore;

  return (
    <div className="flex flex-col gap-5 pt-6">
      <AIVisibilityInsightsSummary />

      <div className="grid gap-4 xl:grid-cols-3">
        <Panel
          title={<Trans>Visibility index</Trans>}
          subtitle={<Trans>We’ll scan and map the product areas we detect.</Trans>}
          className="min-h-64"
        >
          {error ? (
            <WidgetError message={<Trans>Unable to load visibility index.</Trans>} />
          ) : (
            <div className="flex flex-1 items-center justify-center py-3">
              <div
                className="relative flex size-50 flex-col items-center justify-center"
                role={visibilityScore == null ? undefined : 'meter'}
                aria-label={t`Visibility index`}
                aria-valuemin={visibilityScore == null ? undefined : 0}
                aria-valuemax={visibilityScore == null ? undefined : 100}
                aria-valuenow={visibilityScore ?? undefined}
                aria-busy={isLoading}
              >
                <div className="absolute inset-0" aria-hidden="true">
                  <Flare
                    data={[
                      {
                        id: 'visibility-index',
                        name: t`Visibility index`,
                        data: [
                          {
                            score:
                              visibilityScore == null || visibilityScore === 0
                                ? null
                                : visibilityScore / 100,
                          },
                        ],
                      },
                    ]}
                    colors={['var(--color-amber-400)']}
                    width={200}
                    height={200}
                    spacingTop={0}
                    spacingBottom={0}
                    spacingLeft={0}
                    spacingRight={0}
                    noPadding
                    error={error}
                    options={{
                      chart: { width: 200, height: 200, margin: 0, animation: false },
                      plotOptions: { series: { animation: false, enableMouseTracking: false } },
                      tooltip: { enabled: false },
                      accessibility: { enabled: false },
                    }}
                  >
                    <Gauge
                      y="score"
                      innerSize={0.9}
                      backgroundColor="var(--color-gray-200)"
                      rounded
                    />
                  </Flare>
                </div>
                {isLoading ? (
                  <Skeleton className="h-10 w-20" />
                ) : (
                  <span className="relative text-4xl font-semibold text-gray-900">
                    {visibilityScore == null
                      ? '—'
                      : numberFormat(visibilityScore, { precision: 0 })}
                  </span>
                )}
                <span className="relative text-sm text-gray-500">
                  <Trans>/ 100</Trans>
                </span>
              </div>
            </div>
          )}
        </Panel>
        <AIVisibilityProviderChart />
        <AIVisibilityOverviewTrendChart />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <AIVisibilityOverviewCompetitorTable />
        <AIVisibilityOverviewProductAreaChart />
      </div>

      <AIVisibilityOverviewTopGaps />
    </div>
  );
};

export default AIVisibilityOverview;
